import type { CompetitorId } from './types.ts';

/** One flat step in a reconstructed match replay: seat `seatId`'s `localIndex`-th move. */
export type MatchReplayStep = {
  seatId: CompetitorId;
  localIndex: number;
};

/**
 * Reconstructs the alternating turn order of **recorded** race clicks from each seat's final
 * move count alone. Live play may still give an early finisher parking autopilot turns while
 * others race, but those are not stored — so "seat still has moves left" matches "not finished
 * yet" for Top Lists / Show replay. Walking from seat -1 (mirroring `createMatch`'s
 * `activeId: 0`) and wrapping with the same skip rule reproduces that recorded order without
 * the full `MatchState` / `RaceState` machinery.
 */
export function matchReplayOrder(moveCounts: readonly number[]): MatchReplayStep[] {
  const steps: MatchReplayStep[] = [];
  const seatCount = moveCounts.length;

  if (seatCount === 0) {
    return steps;
  }

  const taken = moveCounts.map(() => 0);
  let activeId = nextIdWithRemainingMoves(-1, moveCounts, taken);

  while (activeId !== null) {
    steps.push({ seatId: activeId, localIndex: taken[activeId]! });
    taken[activeId] = taken[activeId]! + 1;
    activeId = nextIdWithRemainingMoves(activeId, moveCounts, taken);
  }

  return steps;
}

/**
 * Buckets the flat alternating order back into rounds: every seat's `localIndex`-th move sits
 * in round `localIndex`, since `matchReplayOrder` visits every still-racing seat exactly once
 * per round, in seat-id order, before moving to the next round. A seat that finished early
 * simply stops appearing in later rounds. Used for the "simultaneous" replay mode, where every
 * seat in a round animates its move at once instead of one seat per flat step.
 */
export function groupReplayStepsByRound(
  steps: readonly MatchReplayStep[],
): MatchReplayStep[][] {
  const rounds: MatchReplayStep[][] = [];

  for (const step of steps) {
    const round = (rounds[step.localIndex] ??= []);
    round.push(step);
  }

  return rounds;
}

/** Same wrap-and-skip walk as `nextActiveId`, driven by move counts instead of `race.complete`. */
function nextIdWithRemainingMoves(
  fromId: number,
  moveCounts: readonly number[],
  taken: readonly number[],
): CompetitorId | null {
  const seatCount = moveCounts.length;

  for (let step = 1; step <= seatCount; step += 1) {
    const id = (fromId + step) % seatCount;

    if ((taken[id] ?? 0) < (moveCounts[id] ?? 0)) {
      return id;
    }
  }

  return null;
}
